import React, { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
  color: string;
  borderColor: string;
  hoverBg: string;
}

const faqs: FAQItem[] = [
  {
    question: "What if I've never coded before?",
    answer: "No problem! While basic Python knowledge is helpful, we welcome beginners. We'll provide learning resources, mentorship, and a supportive community to help you get started. Many successful hackathon projects have come from first-time coders!",
    color: 'text-cyan-500',
    borderColor: 'border-cyan-500',
    hoverBg: 'hover:bg-cyan-50'
  },
  {
    question: "Do I need a team?",
    answer: "You can participate solo or in teams of up to 4 people. While teams can bring diverse skills, solo participants are equally welcome. We'll help you find teammates if you're looking for one!",
    color: 'text-yellow-500',
    borderColor: 'border-yellow-500',
    hoverBg: 'hover:bg-yellow-50'
  },
  {
    question: "Can I start early?",
    answer: "Yes! While the official hackathon runs from June 4-11, you can start learning Python and exploring Preswald anytime. We encourage early preparation and will provide resources to help you get ready.",
    color: 'text-purple-500',
    borderColor: 'border-purple-500',
    hoverBg: 'hover:bg-purple-50'
  },
  {
    question: "What can I build?",
    answer: "Preswald is perfect for building Python-powered data applications! Think interactive dashboards, data visualization tools, machine learning interfaces, or any app that leverages Python's data science ecosystem (pandas, numpy, scikit-learn, etc.). Whether you're analyzing datasets, creating business intelligence tools, or building AI-powered applications, Preswald makes it easy to turn your Python code into a polished web app.",
    color: 'text-red-500',
    borderColor: 'border-red-500',
    hoverBg: 'hover:bg-red-50'
  },
  {
    question: "Do I need to know Preswald before starting?",
    answer: "No prior experience with Preswald is required. We'll provide resources and support to help you get started. However, basic Python knowledge is recommended.",
    color: 'text-cyan-500',
    borderColor: 'border-cyan-500',
    hoverBg: 'hover:bg-cyan-50'
  },
  {
    question: "How will projects be judged?",
    answer: "Projects will be evaluated based on innovation, technical implementation, user experience, and potential impact. A panel of expert judges will review all submissions and select the winners.",
    color: 'text-yellow-500',
    borderColor: 'border-yellow-500',
    hoverBg: 'hover:bg-yellow-50'
  },
  {
    question: "What are the important dates?",
    answer: "The hackathon runs from June 4-11, 2025. Registration is open until June 3rd. Submissions are due by June 18th at 11:59 PM UTC. Winners will be announced on June 15th.",
    color: 'text-purple-500',
    borderColor: 'border-purple-500',
    hoverBg: 'hover:bg-purple-50'
  },
  {
    question: "Can I use existing code?",
    answer: "You can use open-source libraries and frameworks, but the core of your project must be built during the hackathon period. Any pre-existing code must be clearly documented.",
    color: 'text-green-500',
    borderColor: 'border-green-500',
    hoverBg: 'hover:bg-green-50'
  }
];

const HackathonFAQs: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="relative max-w-8xl mx-auto px-4 py-16">
      {/* Semi-transparent overlay */}
      <div className="absolute inset-0 bg-white backdrop-blur-sm" />
      
      <div className="relative z-10">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          Frequently Asked Questions
        </h2>
        
        <div className="space-y-4 max-w-4xl mx-auto">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`group border-2 ${faq.borderColor} bg-white/50 transition-all duration-300 ease-in-out
                hover:shadow-lg hover:-translate-y-1
                group-hover:border-opacity-100 border-opacity-50`}
            >
              <button
                className={`w-full px-6 py-4 text-left flex justify-between items-center ${faq.hoverBg} transition-colors`}
                onClick={() => toggleFAQ(index)}
              >
                <span className="font-medium text-slate-900">{faq.question}</span>
                <span className={`${faq.color} text-xl font-medium transition-transform duration-300 group-hover:scale-110`}>
                  {openIndex === index ? '−' : '+'}
                </span>
              </button>
              
              {openIndex === index && (
                <div className={`px-6 py-4 ${faq.hoverBg}`}>
                  <p className="text-slate-600">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-slate-600">
            Still have questions?{' '}
            <a
              href="mailto:hackathon@structuredlabs.com"
              className="text-slate-900 hover:text-slate-700 underline"
            >
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default HackathonFAQs; 